import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import { useRecorder } from '../hooks/useRecorder';
import { reset as resetRecitation, setRecordingState } from '../store/slices/recitationSlice';
import { submitRecitation } from '../store/slices/evaluationSlice';
import { clearEvaluation } from '../store/slices/evaluationSlice';
import clsx from 'clsx';

const RecordButton: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { recordingState, audioBlob, inputLevel, error } = useSelector(
    (state: RootState) => state.recitation
  );
  const ayah = useSelector((state: RootState) => state.ayah.current);
  const evalLoading = useSelector((state: RootState) => state.evaluation.loading);
  const evalError = useSelector((state: RootState) => state.evaluation.error);
  const { startRecording, stopRecording } = useRecorder();
  const autoSubmittedRef = useRef(false);

  const isProcessing = recordingState === 'processing' || evalLoading;

  // Auto-submit when recording stops
  useEffect(() => {
    if (recordingState === 'done' && audioBlob && ayah && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      dispatch(setRecordingState('processing'));
      // Map difficulty → user_level (the existing knob the main eval already understands)
      // easy → beginner, normal → intermediate, strict → advanced
      const diff = localStorage.getItem('tajweed_practice_difficulty') || 'easy';
      const userLevel = diff === 'easy' ? 'beginner' : diff === 'strict' ? 'advanced' : 'intermediate';
      dispatch(
        submitRecitation({
          audioBlob,
          surah: ayah.surah,
          ayah: ayah.ayah,
          userLevel,
          language: localStorage.getItem('tajweed_session_lang') || 'en',
        })
      );
    }
    if (recordingState === 'idle') {
      autoSubmittedRef.current = false;
    }
  }, [recordingState, audioBlob, ayah, dispatch]);

  const handleMainButton = () => {
    if (recordingState === 'idle' || recordingState === 'error') {
      startRecording();
    } else if (recordingState === 'recording') {
      stopRecording();
    }
  };

  const handleReRecord = () => {
    dispatch(clearEvaluation());
    dispatch(resetRecitation());
  };

  // Amplified level for visual responsiveness (input level is too low from raw avg)
  const level = Math.min(inputLevel * 4, 1);

  return (
    <div className="flex flex-col items-center gap-4 w-full overflow-hidden">
      {/* Main button with animated rings */}
      <div className="relative flex items-center justify-center w-44 h-44">
        {/* Outer pulse rings when recording */}
        {recordingState === 'recording' && (
          <>
            <span
              className="absolute rounded-full bg-red-400/20 transition-all duration-150"
              style={{
                width: `${110 + level * 30}px`,
                height: `${110 + level * 30}px`,
              }}
            />
            <span
              className="absolute rounded-full bg-red-400/10 transition-all duration-200"
              style={{
                width: `${130 + level * 40}px`,
                height: `${130 + level * 40}px`,
              }}
            />
            <span
              className="absolute rounded-full bg-red-400/5 transition-all duration-300"
              style={{
                width: `${150 + level * 50}px`,
                height: `${150 + level * 50}px`,
              }}
            />
          </>
        )}

        {/* Processing rings */}
        {isProcessing && (
          <>
            <span className="absolute w-32 h-32 rounded-full border-2 border-emerald-400/30 animate-ping" />
            <span className="absolute w-28 h-28 rounded-full border border-emerald-400/20 animate-pulse" />
          </>
        )}

        {/* Idle breathing glow */}
        {recordingState === 'idle' && !isProcessing && (
          <span className="absolute w-28 h-28 rounded-full bg-emerald-400/10 animate-pulse" />
        )}

        <button
          data-record-btn
          onClick={handleMainButton}
          disabled={isProcessing || !ayah}
          className={clsx(
            'relative z-10 w-24 h-24 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-4',
            {
              'bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-400 hover:to-emerald-600 text-white focus:ring-emerald-500/50 shadow-lg shadow-emerald-500/25':
                recordingState === 'idle' && !isProcessing,
              'bg-gradient-to-br from-red-500 to-red-700 text-white focus:ring-red-500/50 shadow-lg shadow-red-500/30':
                recordingState === 'recording',
              'bg-gradient-to-br from-emerald-500/60 to-emerald-700/60 text-white/80 cursor-wait':
                isProcessing,
              'bg-red-700 text-red-200': recordingState === 'error',
            }
          )}
          style={
            recordingState === 'recording'
              ? { transform: `scale(${1 + level * 0.08})` }
              : undefined
          }
        >
          {isProcessing ? (
            <svg className="animate-spin h-10 w-10" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : recordingState === 'recording' ? (
            <svg className="h-10 w-10" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          ) : (
            <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 15a3 3 0 003-3V5a3 3 0 00-6 0v7a3 3 0 003 3z" />
            </svg>
          )}
        </button>
      </div>

      {/* Audio level bars when recording */}
      {recordingState === 'recording' && (
        <div className="flex items-end gap-[3px] h-8">
          {Array.from({ length: 16 }).map((_, i) => {
            const barLevel = Math.max(0, level - Math.abs(i - 7.5) * 0.06);
            return (
              <div
                key={i}
                className="w-[3px] rounded-full bg-red-400 transition-all duration-75"
                style={{
                  height: `${4 + barLevel * 28}px`,
                  opacity: 0.4 + barLevel * 0.6,
                }}
              />
            );
          })}
        </div>
      )}

      <p className="text-sm text-gray-500 dark:text-gray-400">
        {isProcessing
          ? 'Analyzing with AI...'
          : recordingState === 'recording'
          ? 'Recording... Tap to stop'
          : recordingState === 'idle'
          ? 'Tap to record'
          : recordingState === 'error'
          ? 'Error occurred'
          : ''}
      </p>

      {/* Re-record button (only shows after eval completes, not during processing) */}
      {!isProcessing && recordingState === 'idle' && evalLoading === false && (
        <button data-rerecord-btn onClick={handleReRecord} className="btn-secondary text-sm hidden">
          Re-record
        </button>
      )}

      {(error || evalError) && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-300 max-w-sm text-center">
          {error || evalError}
        </div>
      )}
    </div>
  );
};

export default RecordButton;
